/**
 * Per-connection LedgerSync sync state: the vocabulary, and how to read it
 * (DCP-401).
 *
 * SHARED BECAUSE THREE RUNTIMES TOUCH THE SAME COLUMNS. `ledgersync_connections`
 * carries two independent status pairs — `sync_status` / `sync_status_at` for
 * the TRANSACTION pipeline and `balance_sync_status` / `balance_sync_status_at`
 * for the BALANCE pipeline. They are written by the Deno edge function (which
 * stamps `queued` the moment the user presses Sync) and by the Node Trigger.dev
 * orchestrators (which move them through `refreshing` → `syncing` →
 * `completed` / `failed`), and they are read by the web app. A status value or
 * a staleness rule that lived in only one of them would drift the first time
 * somebody touched it — and the database CHECK constraints would then reject
 * the write of whichever side was ahead.
 *
 * Dependency-free on purpose: the edge function imports this file directly
 * through the `@dcp/shared/` import map, so it must not pull in anything Deno
 * cannot resolve.
 */

/**
 * Every value the two status columns may hold, in pipeline order.
 *
 * Mirrors the CHECK constraints `ledgersync_connections_sync_status_check` and
 * `ledgersync_connections_balance_sync_status_check` (migration
 * 20260928000000_dcp401_ledgersync_connection_sync_state). Adding a value here
 * without a migration makes every write of it fail at the database.
 *
 * - `queued`     — the edge function enqueued the orchestrator run; nothing
 *                  has started yet.
 * - `refreshing` — the orchestrator is running and waiting on the bank
 *                  re-aggregation (the refresh gate).
 * - `syncing`    — the refresh settled (or was given up on) and the reader is
 *                  pulling data from the vendor.
 * - `completed`  — terminal: the run finished.
 * - `failed`     — terminal: the run ended in an error; the reason is in the
 *                  matching `*_error` column.
 */
export const LEDGERSYNC_SYNC_STATUSES = [
  'queued',
  'refreshing',
  'syncing',
  'completed',
  'failed',
] as const

/** One value of {@link LEDGERSYNC_SYNC_STATUSES}. */
export type LedgerSyncSyncStatus = (typeof LEDGERSYNC_SYNC_STATUSES)[number]

/**
 * The non-terminal statuses — the ones that mean "a sync is in flight" as long
 * as they are fresh (see {@link isLedgerSyncSyncActive}).
 */
export const LEDGERSYNC_ACTIVE_SYNC_STATUSES = [
  'queued',
  'refreshing',
  'syncing',
] as const satisfies readonly LedgerSyncSyncStatus[]

/**
 * The terminal statuses — a run that reached one of them is over. Derived from
 * the two lists above so a status added to either cannot be forgotten here.
 */
export const LEDGERSYNC_TERMINAL_SYNC_STATUSES = Object.freeze(
  LEDGERSYNC_SYNC_STATUSES.filter(
    (status) => !(LEDGERSYNC_ACTIVE_SYNC_STATUSES as readonly string[]).includes(status),
  ),
)

/** The two independent pipelines a connection carries a status triple for. */
export const LEDGERSYNC_SYNC_PIPELINES = ['transactions', 'balances'] as const

/** One of {@link LEDGERSYNC_SYNC_PIPELINES}. */
export type LedgerSyncSyncPipeline = (typeof LEDGERSYNC_SYNC_PIPELINES)[number]

/**
 * Which `ledgersync_connections` columns hold each pipeline's state — the ONE
 * place the naming lives. The edge function and the tasks build their writes
 * from it, and the web app reads rows through {@link readLedgerSyncPipeline},
 * so a renamed column or a third pipeline is a change to this map alone.
 */
export const LEDGERSYNC_SYNC_COLUMNS = {
  transactions: {
    status: 'sync_status',
    statusAt: 'sync_status_at',
    error: 'sync_error',
    runId: 'sync_run_id',
  },
  balances: {
    status: 'balance_sync_status',
    statusAt: 'balance_sync_status_at',
    error: 'balance_sync_error',
    runId: 'balance_sync_run_id',
  },
} as const satisfies Record<LedgerSyncSyncPipeline, { status: string; statusAt: string; error: string; runId: string }>

/**
 * Any of the six STATE column names in {@link LEDGERSYNC_SYNC_COLUMNS} — what
 * readers see. `runId` is left out on purpose: it names the run that owns the
 * phase, which only the writers use, and the read model does not return it.
 */
export type LedgerSyncSyncColumn =
  (typeof LEDGERSYNC_SYNC_COLUMNS)[LedgerSyncSyncPipeline]['status' | 'statusAt' | 'error']

/**
 * The shape {@link readLedgerSyncPipeline} reads: anything carrying the six
 * columns. Structural so that neither runtime's own row type has to live here.
 */
export type LedgerSyncSyncStateRow = Readonly<Record<LedgerSyncSyncColumn, string | null | undefined>>

/** One pipeline's state, as {@link readLedgerSyncPipeline} returns it. */
export interface LedgerSyncPipelineState {
  status: string | null
  statusAt: string | null
  error: string | null
}

/**
 * How long a NON-TERMINAL status may sit unchanged before readers treat it as
 * dead, in milliseconds. Measured from `*_status_at`, i.e. from the LAST
 * transition, not from when the user pressed Sync.
 *
 * WHY A CEILING AT ALL. The terminal `failed` is written by the orchestrator's
 * `onFailure` hook, and Trigger.dev does NOT run `onFailure` for a run that
 * Crashed (worker killed: OOM, machine lost), was Canceled, or hit a System
 * failure — nor for a run that expired in its queue before ever starting. In
 * every one of those cases the row is left at `queued` / `refreshing` /
 * `syncing` forever, and without a ceiling the UI would show a spinner that
 * never ends. Readers therefore treat a status older than this as "not
 * running" rather than trusting it.
 *
 * WHY 30 MINUTES — THE LONGEST SINGLE PHASE, PLUS SLACK. Because the clock
 * restarts at every transition, the number to beat is the longest time ONE
 * phase can legitimately take, not the whole run:
 *
 *   `refreshing` (the refresh gate, ./refresh-gate in @dcp/tasks)
 *       REFRESH_RUN_TTL        10m — the refresh run may sit queued on
 *                                    `ledgersync-refresh` (concurrency 5)
 *                                    before it is expired unstarted
 *     + MAX_BUDGET_SECONDS     15m — the refresh wait itself
 *                                    (./refresh-connection, 900s ceiling)
 *     = 25m
 *
 *   `syncing` (the reader leg, ./sync-connection and
 *   ./sync-balances-connection)
 *       READER_RUN_TTL         10m — the reader may sit queued on
 *                                    `ledgersync-api` (concurrency 5)
 *     + READ_*_MAX_DURATION    10m — the reader runs (600s; the readers never
 *                                    park at a waitpoint, so their CPU ceiling
 *                                    is close to wall time)
 *     = 20m
 *
 * The orchestrators' own `maxDuration` (600s) does NOT bound either phase:
 * it is CPU time and explicitly excludes the time parked in `triggerAndWait`,
 * which is where both phases spend nearly all of their wall time.
 *
 * `refreshing` wins at 25m. The remaining 5m (20%) absorbs what the terms
 * above leave out: the 60s timeout on the refresh POST and the 30s timeout on
 * each poll's reads (the budget counts scheduled wait, not HTTP time), the
 * orchestrator's housekeeping before it reaches the gate, the ≤30s retry
 * backoff between orchestrator attempts (each attempt re-stamps its phase),
 * and a couple of minutes of clock skew between the worker that stamps
 * `*_status_at` and the browser that reads it.
 *
 * KNOWN GAP — `queued` IS ONLY AS BOUNDED AS ITS ENQUEUE. The orchestrators
 * sit on `ledgersync-wait` (concurrency 10), and a run triggered WITHOUT a
 * `ttl` can wait there indefinitely behind a 07:00 sweep. Give the enqueue a
 * TTL no longer than this ceiling and the phase is bounded like the others;
 * without one, a long queue shows as "not running" until the run starts and
 * stamps `refreshing`, which is the harmless direction to be wrong in.
 *
 * WHY NOT LONGER. The cost of a too-long ceiling is a dead run that keeps the
 * Sync button looking busy for that long; the cost of a too-short one is a
 * live run briefly shown as idle until its next transition re-stamps it. The
 * second is self-correcting, the first is not, so the ceiling sits as close to
 * the real maximum as the slack allows.
 */
export const LEDGERSYNC_SYNC_STALL_MS = 30 * 60 * 1000

/** Set lookup behind {@link isLedgerSyncSyncStatus}. */
const SYNC_STATUS_SET: ReadonlySet<string> = new Set(LEDGERSYNC_SYNC_STATUSES)

/** Set lookup behind {@link isLedgerSyncSyncActive} and {@link isLedgerSyncSyncStalled}. */
const ACTIVE_SYNC_STATUS_SET: ReadonlySet<string> = new Set(LEDGERSYNC_ACTIVE_SYNC_STATUSES)

/** Set lookup behind {@link isLedgerSyncSyncTerminal}. */
const TERMINAL_SYNC_STATUS_SET: ReadonlySet<string> = new Set(LEDGERSYNC_TERMINAL_SYNC_STATUSES)

/**
 * Narrow an arbitrary value (a DB column, a JSON field) to a known status.
 *
 * @param value - Anything
 * @returns true when `value` is one of {@link LEDGERSYNC_SYNC_STATUSES}
 */
export function isLedgerSyncSyncStatus(value: unknown): value is LedgerSyncSyncStatus {
  return typeof value === 'string' && SYNC_STATUS_SET.has(value)
}

/**
 * Is a sync of this pipeline genuinely in flight right now?
 *
 * True only when the status is non-terminal AND it was stamped less than
 * {@link LEDGERSYNC_SYNC_STALL_MS} ago. Everything else — NULL (the row was
 * never touched by the tracked path), a terminal status, an unknown value, a
 * missing or unparseable timestamp, or a status that has gone stale — is
 * "not running". An unparseable timestamp counts as stale rather than fresh
 * because a status we cannot age is a status we cannot tell from a crashed
 * run's leftovers.
 *
 * A timestamp slightly in the FUTURE (worker clock ahead of the reader's)
 * yields a negative age and reads as active, which is correct: it was written
 * moments ago.
 *
 * @param status - `sync_status` or `balance_sync_status`
 * @param statusAt - The matching `*_status_at`, as an ISO-8601 string
 * @param now - Epoch milliseconds to measure against; defaults to the current time
 * @returns true when the pipeline should be shown as running
 */
export function isLedgerSyncSyncActive(
  status: string | null | undefined,
  statusAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!status || !ACTIVE_SYNC_STATUS_SET.has(status)) return false
  if (!statusAt) return false
  const stampedAt = Date.parse(statusAt)
  if (Number.isNaN(stampedAt)) return false
  return now - stampedAt < LEDGERSYNC_SYNC_STALL_MS
}

/**
 * Did the run that stamped this status end without telling us — a crash, a
 * cancel, an expiry — so its non-terminal status is a leftover rather than a
 * sync in flight?
 *
 * Exactly the non-terminal statuses {@link isLedgerSyncSyncActive} refuses:
 * too old, or with a timestamp that cannot be aged. NULL, unknown and terminal
 * statuses are never "stalled" — nothing claimed to be running.
 *
 * @param status - `sync_status` or `balance_sync_status`
 * @param statusAt - The matching `*_status_at`, as an ISO-8601 string
 * @param now - Epoch milliseconds to measure against; defaults to the current time
 * @returns true when a non-terminal status is no longer live
 */
export function isLedgerSyncSyncStalled(
  status: string | null | undefined,
  statusAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  return !!status && ACTIVE_SYNC_STATUS_SET.has(status) && !isLedgerSyncSyncActive(status, statusAt, now)
}

/**
 * Has the run that stamped this status finished (either way)?
 *
 * @param status - `sync_status` or `balance_sync_status`
 * @returns true for `completed` / `failed`; false for NULL, unknown and in-flight values
 */
export function isLedgerSyncSyncTerminal(status: string | null | undefined): boolean {
  return !!status && TERMINAL_SYNC_STATUS_SET.has(status)
}

/**
 * Read one pipeline's status triple off a row, through
 * {@link LEDGERSYNC_SYNC_COLUMNS}. Missing fields come back as `null`, so a
 * row from a database without the DCP-401 columns reads as "never synced".
 *
 * @param row - Any object carrying the six sync-state columns
 * @param pipeline - Which pipeline to read
 * @returns That pipeline's status, stamp and error
 */
export function readLedgerSyncPipeline(
  row: LedgerSyncSyncStateRow,
  pipeline: LedgerSyncSyncPipeline,
): LedgerSyncPipelineState {
  const columns = LEDGERSYNC_SYNC_COLUMNS[pipeline]
  return {
    status: row[columns.status] ?? null,
    statusAt: row[columns.statusAt] ?? null,
    error: row[columns.error] ?? null,
  }
}
